import React, { useState } from 'react';
import axios from 'axios';
import { InformationOutline } from 'mdi-material-ui';

import CardDialog from '~/components/CardDialog';
import { Text } from '~/components/core';
import Tooltip from '~/components/core/Atomic/Tooltip';
import LoadingSwitch from '~/components/core/Loading/LoadingSwitch';
import ConfigurableOption from '~/components/core/Molecules/ConfigurableOption';
import Heading from '~/components/core/TextComponents/Heading';
import useOrganization from '~/components/OrganizationContext';
import { useSysconfig } from '~/components/SystemConfiguration/SystemConfigurationScreen';
import Divider from '~/components/ui/atoms/Divider';
import useDataFetcher from '~/components/useDataFetcher';
import type { CommunicationConfigurationInfo } from '~/server_shared/openapi/data-contracts';
import { displayCommunicationCategories, reportAxiosError } from '~/Utils';

import CommunicationCategoriesSection from './CommunicationCategoriesSection';
import TEXTS from './texts';

const getConfigRoute = (organizationId: number) =>
  `/sysconfig/api/v1/organizations/${organizationId}/communication_configuration`;

const CommunicationGeneralConfig: React.FC = () => {
  // @ts-expect-error TODO: useSysconfig hook needs TypeScript definition
  const { organization: sysconfigOrganization } = useSysconfig();
  // @ts-expect-error TODO: useOrganization hook needs TypeScript definition
  const { reloadCommunicationConfiguration, communicationCategories } = useOrganization();

  const {
    isError: isConfigError,
    isLoading: isConfigLoading,
    data: config,
    reloadData: reloadConfig,
  } = useDataFetcher<CommunicationConfigurationInfo>(getConfigRoute(sysconfigOrganization.id));

  // With no active category the send forms hide the picker, so requiring one would block every send.
  // Turning the switch off stays possible.
  const isCategoryRequiredBlocked =
    !config?.is_category_required && !displayCommunicationCategories(communicationCategories);

  const [isUpdatingHandledConfig, setIsUpdatingHandledConfig] = useState(false);
  const [isUpdatingCategoryRequiredConfig, setIsUpdatingCategoryRequiredConfig] = useState(false);
  const [isUpdatingAdjusterPagePendingDefault, setIsUpdatingAdjusterPagePendingDefault] = useState(false);
  const [isUpdatingClaimTabPendingDefault, setIsUpdatingClaimTabPendingDefault] = useState(false);
  const [isUpdatingPermissionRequiredConfig, setIsUpdatingPermissionRequiredConfig] = useState(false);

  const reloadAll = async () => {
    await reloadConfig();
    await reloadCommunicationConfiguration?.();
  };

  const handleToggleHandledStatus = async (isEnabled: boolean) => {
    try {
      setIsUpdatingHandledConfig(true);
      await axios.patch(getConfigRoute(sysconfigOrganization.id), { is_handled_status_enabled: isEnabled });
      await reloadAll();
    } catch (err) {
      await reportAxiosError(err);
    } finally {
      setIsUpdatingHandledConfig(false);
    }
  };

  const handleToggleCategoryRequired = async (isEnabled: boolean) => {
    try {
      setIsUpdatingCategoryRequiredConfig(true);
      await axios.patch(getConfigRoute(sysconfigOrganization.id), { is_category_required: isEnabled });
      await reloadAll();
    } catch (err) {
      await reportAxiosError(err);
    } finally {
      setIsUpdatingCategoryRequiredConfig(false);
    }
  };

  const handleToggleAdjusterPagePendingDefault = async (isEnabled: boolean) => {
    try {
      setIsUpdatingAdjusterPagePendingDefault(true);
      await axios.patch(getConfigRoute(sysconfigOrganization.id), {
        adjuster_page_handled_filter_defaults_to_pending: isEnabled,
      });
      await reloadAll();
    } catch (err) {
      await reportAxiosError(err);
    } finally {
      setIsUpdatingAdjusterPagePendingDefault(false);
    }
  };

  const handleToggleClaimTabPendingDefault = async (isEnabled: boolean) => {
    try {
      setIsUpdatingClaimTabPendingDefault(true);
      await axios.patch(getConfigRoute(sysconfigOrganization.id), {
        claim_tab_handled_filter_defaults_to_pending: isEnabled,
      });
      await reloadAll();
    } catch (err) {
      await reportAxiosError(err);
    } finally {
      setIsUpdatingClaimTabPendingDefault(false);
    }
  };

  const handleTogglePermissionRequired = async (isEnabled: boolean) => {
    try {
      setIsUpdatingPermissionRequiredConfig(true);
      await axios.patch(getConfigRoute(sysconfigOrganization.id), {
        is_communication_permission_required_for_all_contacts: isEnabled,
      });
      await reloadAll();
    } catch (err) {
      await reportAxiosError(err);
    } finally {
      setIsUpdatingPermissionRequiredConfig(false);
    }
  };

  return (
    <CardDialog title="Communication General Settings">
      <LoadingSwitch isLoading={isConfigLoading} isError={isConfigError}>
        <ConfigurableOption
          isEnabled={config?.is_handled_status_enabled}
          onChangeToggle={handleToggleHandledStatus}
          showOnly={isUpdatingHandledConfig}
          configurationView={
            <div>
              <Heading variant={Heading.TYPES.H3}>Handled Status</Heading>
              <Text variant="small" weight="regular">
                When enabled, incoming communications start as Pending and users can mark them as handled.
              </Text>
            </div>
          }
        />
      </LoadingSwitch>

      {config?.is_handled_status_enabled && (
        <>
          <Divider className="my-20" />
          <LoadingSwitch isLoading={isConfigLoading} isError={isConfigError}>
            <ConfigurableOption
              isEnabled={config?.adjuster_page_handled_filter_defaults_to_pending}
              onChangeToggle={handleToggleAdjusterPagePendingDefault}
              showOnly={isUpdatingAdjusterPagePendingDefault}
              configurationView={
                <div>
                  <Heading variant={Heading.TYPES.H3}>Default Communications Page Filter to Pending</Heading>
                  <Text variant="small" weight="regular">
                    When enabled, the Communications page opens showing only pending communications. Users can switch to
                    Handled or All at any time.
                  </Text>
                </div>
              }
            />
          </LoadingSwitch>
          <Divider className="my-20" />
          <LoadingSwitch isLoading={isConfigLoading} isError={isConfigError}>
            <ConfigurableOption
              isEnabled={config?.claim_tab_handled_filter_defaults_to_pending}
              onChangeToggle={handleToggleClaimTabPendingDefault}
              showOnly={isUpdatingClaimTabPendingDefault}
              configurationView={
                <div>
                  <Heading variant={Heading.TYPES.H3}>Default Claim Communications Tab Filter to Pending</Heading>
                  <Text variant="small" weight="regular">
                    When enabled, the Communications tab on a claim opens showing only pending communications. Users can
                    switch to Handled or All at any time.
                  </Text>
                </div>
              }
            />
          </LoadingSwitch>
        </>
      )}

      <Divider className="my-20" />
      <LoadingSwitch isLoading={isConfigLoading} isError={isConfigError}>
        <ConfigurableOption
          isEnabled={config?.is_communication_permission_required_for_all_contacts}
          onChangeToggle={handleTogglePermissionRequired}
          showOnly={isUpdatingPermissionRequiredConfig}
          configurationView={
            <div>
              <Heading variant={Heading.TYPES.H3}>{TEXTS.communicationPermissionRequiredForAllContacts.title}</Heading>
              <Text variant="small" weight="regular">
                {TEXTS.communicationPermissionRequiredForAllContacts.description}
              </Text>
            </div>
          }
        />
      </LoadingSwitch>

      <Divider className="my-20" />
      <LoadingSwitch isLoading={isConfigLoading} isError={isConfigError}>
        <ConfigurableOption
          isEnabled={config?.is_category_required}
          onChangeToggle={handleToggleCategoryRequired}
          showOnly={isUpdatingCategoryRequiredConfig || isCategoryRequiredBlocked}
          configurationView={
            <div className="flex items-center gap-8">
              <div>
                <Heading variant={Heading.TYPES.H3}>Require Category on Communications</Heading>
                <Text variant="small" weight="regular">
                  When enabled, users must assign a category to every outgoing communication.
                </Text>
              </div>
              <Tooltip
                title={
                  isCategoryRequiredBlocked
                    ? TEXTS.categoryRequired.blockedTooltip
                    : 'When enabled, users must assign a category to every outgoing communication. Communications cannot be sent without selecting a category.'
                }
              >
                <InformationOutline fontSize="small" className="text-slate-500" />
              </Tooltip>
            </div>
          }
        />
      </LoadingSwitch>
      <Divider className="my-20" />
      <CommunicationCategoriesSection />
    </CardDialog>
  );
};

export default CommunicationGeneralConfig;
